// src/pages/onboarding/BlogPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjects } from '../../contexts/ProjectContext';
import api from '../../utils/apiClient';
import { BarChart2 } from 'lucide-react';

const BlogPage = ({ isSettings = false }) => {
  const navigate = useNavigate();
  const { currentProject, loadCurrentProject } = useProjects();

  const [sitemapUrl, setSitemapUrl] = useState('');
  const [blogRootUrl, setBlogRootUrl] = useState('');
  const [examples, setExamples] = useState(['', '', '']);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState('');

  // Load from /blog-info if it exists
  useEffect(() => {
    const load = async () => {
      if (!currentProject?.id) return;
      try {
        setInitialLoading(true);
        const res = await api.projects.getBlogInfo(currentProject.id);
        const info = res.data || {};
        setSitemapUrl(info.sitemap_url || '');
        setBlogRootUrl(info.blog_root_url || '');
        setExamples([
          info.best_article_examples?.[0] || '',
          info.best_article_examples?.[1] || '',
          info.best_article_examples?.[2] || '',
        ]);
      } catch (err) {
        // Error already logged in console
      } finally {
        setInitialLoading(false);
      }
    };
    load();
  }, [currentProject?.id]);

  const handleExampleChange = (idx, val) => {
    setExamples(exs => {
      const copy = [...exs];
      copy[idx] = val;
      return copy;
    });
  };

  // Function to detect links from the sitemap
  const detectLinksFromSitemap = async (projectId, sitemapUrl) => {
    if (!sitemapUrl) return;
    
    try {
      await api.projects.detectLinks(projectId, {
        source: 'sitemap',
        url: sitemapUrl
      });
    } catch (err) {
      // Error handling is done silently to not interrupt onboarding flow
    }
  };

  // Save blog info on submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!currentProject?.id) {
      setError('No project selected');
      return;
    }

    setLoading(true);
    try {
      // First save the blog info
      await api.projects.updateBlogInfo(currentProject.id, {
        sitemap_url: sitemapUrl,
        blog_root_url: blogRootUrl,
        best_article_examples: examples,
      });

      await loadCurrentProject(currentProject.id, { force: true });

      // If this is the final step of onboarding (not settings page)
      // and we have a sitemap URL, detect links automatically
      if (!isSettings && sitemapUrl) {
        await detectLinksFromSitemap(currentProject.id, sitemapUrl);
      }
      
      // Navigate to the next step
      if (!isSettings) {
        navigate('/onboarding/articles');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-8">
      {!isSettings && (
        <h1 className="text-2xl font-bold text-center text-gray-900 mb-8">
          Set up your Blog
        </h1>
      )}

      <form
        onSubmit={handleSubmit}
        className="border border-gray-200 rounded-lg bg-white shadow-sm p-6 space-y-6"
      >
        <div className="flex items-center mb-4">
          <BarChart2 className="w-5 h-5 text-yellow-500 mr-2" />
          <h2 className="text-lg font-semibold">Blog Information</h2>
        </div>

        <p className="text-gray-600 mb-4">
          Provide details about your blog to help us better understand your content and generate more relevant content.
        </p>

        {/* Sitemap URL */}
        <div>
          <label htmlFor="sitemap" className="block text-sm font-medium text-gray-700 mb-1">
            Sitemap URL
          </label>
          <input
            type="url"
            id="sitemap"
            placeholder="https://yourblog.com/sitemap.xml"
            value={sitemapUrl}
            onChange={e => setSitemapUrl(e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
          <p className="mt-1 text-xs text-gray-500">
            We'll automatically scan your sitemap to find links for internal linking after onboarding
          </p>
        </div>

        {/* Blog Root URL */}
        <div>
          <label htmlFor="root" className="block text-sm font-medium text-gray-700 mb-1">
            Blog Root URL
          </label>
          <input
            type="url"
            id="root"
            placeholder="https://yourblog.com/blog"
            value={blogRootUrl}
            onChange={e => setBlogRootUrl(e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Best Article Examples */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Your Best Article Examples
          </label>
          {examples.map((ex, idx) => (
            <input
              key={idx}
              type="url"
              placeholder={`Article URL #${idx + 1}`}
              value={ex}
              onChange={e => handleExampleChange(idx, e.target.value)}
              className="w-full mt-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          ))}
        </div>

        {error && (
          <div className="bg-red-100 text-red-700 p-2 rounded">{error}</div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-primary-500 text-white rounded-md hover:bg-primary-600 disabled:opacity-50 transition"
        >
          {loading ? 'Saving…' : isSettings ? 'Save Changes' : 'Continue'}
        </button>
      </form>
    </div>
  );
};

export default BlogPage;
